import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../css/EmailSelector.css';
import api from './api';

function EmailSelector({ onSelectEmails }) {
  const [emails, setEmails] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [filter, setFilter] = useState('');
  const [ownerRenterFilter, setOwnerRenterFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(100);
  const [retryCount, setRetryCount] = useState(0);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    fetchEmails();
  }, [retryCount]);

  const fetchEmails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Geen authenticatietoken gevonden. Log opnieuw in.');
        setLoading(false);
        return;
      }
      
      // Try the get-all endpoint
      const response = await api.get(`${apiUrl}/api/bewoners/get-all`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.status === 200 && Array.isArray(response.data)) {
        // Filter only records with email fields
        const emailRecords = response.data.filter(record => 
          record.email || record.email2
        );
        
        // Extract unique categories
        const categories = [...new Set(
          emailRecords
            .map(item => item.category)
            .filter(Boolean)
        )];
        
        setAvailableCategories(categories);
        setEmails(emailRecords);
        console.log(`Loaded ${emailRecords.length} email records from ${response.data.length} total records`);
      } else {
        console.error('Failed to fetch emails: response data is not an array');
        setError('Kon e-mailadressen niet laden');
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
      setError(`Fout bij het ophalen van e-mailadressen: ${error.message || 'Onbekende fout'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmail = (emailObj) => {
    setSelectedEmails((prev) => {
      // Check both primary and secondary emails
      const hasPrimary = prev.includes(emailObj.email);
      const hasSecondary = emailObj.email2 && prev.includes(emailObj.email2);
      
      // If both emails are selected, remove both
      if (hasPrimary && (hasSecondary || !emailObj.email2)) {
        return prev.filter(e => e !== emailObj.email && e !== emailObj.email2);
      }
      
      // If only primary is selected and there's a secondary, add secondary
      if (hasPrimary && emailObj.email2 && !hasSecondary) {
        return [...prev, emailObj.email2];
      }
      
      // If only secondary is selected, remove it and add primary
      if (hasSecondary && !hasPrimary) {
        return prev.filter(e => e !== emailObj.email2).concat(emailObj.email);
      }
      
      // Otherwise, add primary email
      return [...prev, emailObj.email];
    });
  };

  const handleSelectAllEmails = (emailObj) => {
    setSelectedEmails((prev) => {
      // Add both emails if neither is selected
      const hasPrimary = prev.includes(emailObj.email);
      const hasSecondary = emailObj.email2 && prev.includes(emailObj.email2);
      
      if (!hasPrimary && !hasSecondary) {
        const result = [...prev, emailObj.email];
        if (emailObj.email2) result.push(emailObj.email2);
        return result;
      }
      
      // Remove both emails if either is selected
      return prev.filter(e => e !== emailObj.email && e !== emailObj.email2);
    });
  };

  const handleSelectAll = () => {
    const allEmails = [];
    
    // Include ALL emails after filtering
    filteredEmails.forEach(email => {
      if (email.email) allEmails.push(email.email);
      if (email.email2) allEmails.push(email.email2);
    });
    
    setSelectedEmails(allEmails);
    onSelectEmails(allEmails);
  };

  const handleDeselectAll = () => {
    setSelectedEmails([]);
    onSelectEmails([]);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleOwnerRenterFilterChange = (e) => {
    const { value } = e.target;
    setOwnerRenterFilter(prev => 
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const handleCategoryFilterChange = (e) => {
    const { value } = e.target;
    setCategoryFilter(prev => 
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  // Function to get display name for category
  const getCategoryDisplayName = (category) => {
    if (category === 'VVE5toren') return '5 Towers';
    return category;
  };

  // Apply filters to the emails
  const filteredEmails = emails.filter(email => {
    // Check if nummer, naam, or email matches text filter
    const textMatch = filter === '' || (
      (email.nummer && String(email.nummer).toLowerCase().includes(filter.toLowerCase())) ||
      (email.naam && email.naam.toLowerCase().includes(filter.toLowerCase())) ||
      (email.email && email.email.toLowerCase().includes(filter.toLowerCase())) ||
      (email.email2 && email.email2.toLowerCase().includes(filter.toLowerCase()))
    );
    
    // Check if rol matches owner/renter filter
    const roleMatch = ownerRenterFilter.length === 0 || ownerRenterFilter.includes(email.rol);
    
    // Check if category matches building filter
    const categoryMatch = categoryFilter.length === 0 || (
      email.category && categoryFilter.includes(email.category)
    );
    
    // Handle inhabited field
    const isInhabited = email.inhabited === true || email.inhabited === "true" || email.inhabited === "TRUE";
    const isUnInhabited = email.inhabited === false || email.inhabited === "false" || email.inhabited === "FALSE";
    const inhabitedStatus = isInhabited ? true : (isUnInhabited ? false : true);
    
    return textMatch && roleMatch && categoryMatch && inhabitedStatus;
  });
  
  // Get the total number of emails in the filtered list
  const totalEmailCount = filteredEmails.reduce((count, email) => {
    return count + (email.email ? 1 : 0) + (email.email2 ? 1 : 0);
  }, 0);

  // Show load more button if we're not showing all emails
  const showingAllEmails = displayLimit >= filteredEmails.length;
  
  // Get the emails to display based on the current limit
  const displayedEmails = filteredEmails.slice(0, displayLimit);

  // Load more emails function
  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 50); 
  };

  // Update parent component whenever selection changes
  useEffect(() => {
    onSelectEmails(selectedEmails);
  }, [selectedEmails, onSelectEmails]);

  // Function to retry fetching
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="email-selector-container">
        <div className="loading-message">
          <p>E-mailadressen laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-selector-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="email-selector-container">
      <div className="email-selector-header">
        <input
          type="text"
          placeholder="Filter op naam, appartement, of e-mail"
          value={filter}
          onChange={handleFilterChange}
          className="filter-input"
        />
        
        <div className="filter-sections">
          {/* Owner/Renter Filter */}
          <div className="filter-section">
            <h4>Rol</h4>
            <div className="checkbox-group">
              <div className="checkbox-wrapper-24">
                <input
                  id="owner-renter-eigenaar"
                  type="checkbox"
                  value="Eigenaar"
                  checked={ownerRenterFilter.includes('Eigenaar')}
                  onChange={handleOwnerRenterFilterChange}
                />
                <label htmlFor="owner-renter-eigenaar">
                  <span></span>Eigenaar
                </label>
              </div>
              <div className="checkbox-wrapper-24">
                <input
                  id="owner-renter-huurder"
                  type="checkbox"
                  value="Huurder"
                  checked={ownerRenterFilter.includes('Huurder')}
                  onChange={handleOwnerRenterFilterChange}
                />
                <label htmlFor="owner-renter-huurder">
                  <span></span>Huurder
                </label>
              </div>
            </div>
          </div>
          
          {/* Building Category Filter */}
          <div className="filter-section">
            <h4>Categorie</h4>
            <div className="checkbox-group">
              {availableCategories.map(category => (
                <div className="checkbox-wrapper-24" key={`category-${category}`}>
                  <input
                    id={`category-${category}`}
                    type="checkbox"
                    value={category}
                    checked={categoryFilter.includes(category)}
                    onChange={handleCategoryFilterChange}
                  />
                  <label htmlFor={`category-${category}`}>
                    <span></span>{getCategoryDisplayName(category)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="button-group">
          <button onClick={handleSelectAll} className="selector-button">
            Selecteer Alles ({totalEmailCount})
          </button>
          <button onClick={handleDeselectAll} className="selector-button">
            Deselecteer Alles
          </button>
        </div>
      </div>
      
      <div className="email-list-container">
        {filteredEmails.length === 0 ? (
          <p className="no-results">Geen e-mailadressen gevonden met deze filters</p>
        ) : (
          <>
            {displayedEmails.map((emailObj, index) => (
              <div key={index} className="email-item">
                <div className="email-header">
                  <span className="email-name">{emailObj.naam}</span>
                  <span className="email-apartment">{emailObj.nummer}</span>
                  <span className="email-role">{emailObj.rol}</span>
                  {emailObj.category && (
                    <span className="email-category">{getCategoryDisplayName(emailObj.category)}</span>
                  )}
                </div>
                
                <div className="email-addresses">
                  {emailObj.email && (
                    <label className="email-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedEmails.includes(emailObj.email)}
                        onChange={() => handleSelectEmail(emailObj)}
                      />
                      <span className="email-address">{emailObj.email}</span>
                    </label>
                  )}
                  
                  {emailObj.email2 && (
                    <label className="email-checkbox secondary">
                      <input
                        type="checkbox"
                        checked={selectedEmails.includes(emailObj.email2)}
                        onChange={() => handleSelectEmail({...emailObj, email: emailObj.email2})}
                      />
                      <span className="email-address">{emailObj.email2}</span>
                    </label>
                  )}
                </div>
                
                <button 
                  className="select-all-button"
                  onClick={() => handleSelectAllEmails(emailObj)}
                  title={
                    selectedEmails.includes(emailObj.email) || 
                    (emailObj.email2 && selectedEmails.includes(emailObj.email2)) 
                      ? "Deselecteer alle e-mails voor deze bewoner" 
                      : "Selecteer alle e-mails voor deze bewoner"
                  }
                >
                  {selectedEmails.includes(emailObj.email) || 
                   (emailObj.email2 && selectedEmails.includes(emailObj.email2)) 
                    ? "➖" : "➕"}
                </button>
              </div>
            ))}
            
            {/* Load more button */}
            {!showingAllEmails && (
              <div className="load-more-container">
                <button onClick={handleLoadMore} className="load-more-button">
                  Laad meer... (Weergave {displayedEmails.length} van {filteredEmails.length})
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      <div className="selection-summary">
        <span>Geselecteerd: {selectedEmails.length} e-mailadressen</span>
      </div>
    </div>
  );
}

export default EmailSelector;